import React, { useEffect, useState } from "react";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../store/adminData/adminData";
import ScreenHeader from "../../../../CommonComponents/ScreenHeader";
import DescriptionText from "../../../../CommonComponents/DescriptionText";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../services/firebaseService/connection";
import {
  copyToClipboard,
  getDayjsFromTimestamp,
  isMobile,
} from "../../../../utils/helper";
import { getCurrencyUnicode } from "../../../../helperFunctions/util";

import logger from "../../../../utils/logger";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MailOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Table, Tooltip, Avatar } from "antd";
import ChantPopConfirm from "../../../../CommonComponents/ChantTooltip/ChantPopConfirm";
import useGlobalStore from "../../../../store/store";
import ChantButton from "../../../../CommonComponents/ChantButton/ChantButton";
import { toast } from "react-toastify";
import { deleteAdminGiveaway } from "../../../../services/firebaseService/endPoints/admin/content/giveaway";
import { IconContainer } from "../../../../CommonComponents/index.js";
import useMembersData from "../../members/Members/useMembersData.jsx";

const AdminGiveaways = () => {
  const [allGiveaways, setAllGiveaways] = useState([]);
  const [loading, setLoading] = useState(false);

  const groupData = useAdminDataStore(selectCurrentGroupData);

  const { setSelectedGiveawayDetails } = useGlobalStore.getState().giveaways;

  let groupCurrencyISO = groupData.paymentDetails
    ? groupData.paymentDetails.currency || "USD"
    : "USD";
  let groupCurrency = getCurrencyUnicode(groupCurrencyISO);

  const { membersByMemberId, membersLoading } = useMembersData();

  const navigate = useNavigate();

  const handleViewPollDetails = (data) => {
    setSelectedGiveawayDetails(data);
    navigate(`/admin/content/giveaways/${data?.id}`);
  };

  const columns = [
    {
      title: "Actions",
      width: 100,
      align: "center",
      render: (data) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
            }}
          >
            {data && data.id && (
              <i
                className="ml-3"
                style={{ cursor: "pointer" }}
                onClick={() => handleViewPollDetails(data)}
              >
                <EyeOutlined />
              </i>
            )}

            {data && data.id && (
              <i
                className="ml-3"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(`/admin/content/giveaways/${data?.id}/edit`)
                }
              >
                <EditOutlined />
              </i>
            )}

            {data && data.id && (
              <>
                {data?.receivedAmount > 0 ? (
                  <Tooltip title="Cannot delete: payments have been collected">
                    <i
                      className="ml-3"
                      style={{
                        cursor: "not-allowed",
                        color: "gray",
                      }}
                    >
                      <DeleteOutlined />
                    </i>
                  </Tooltip>
                ) : (
                  <ChantPopConfirm
                    title="Delete this giveaway?"
                    description={
                      data?.entrantsCount > 0
                        ? `This giveaway has ${data.entrantsCount} entrant${data.entrantsCount === 1 ? "" : "s"}. This action cannot be undone.`
                        : "This action cannot be undone."
                    }
                    onConfirm={() => handleDeleteGiveaway(data?.id)}
                    okText="Yes, delete"
                    cancelText="No"
                    okButtonProps={{ type: "default" }}
                    cancelButtonProps={{ type: "primary" }}
                  >
                    <i
                      className="ml-3"
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <DeleteOutlined />
                    </i>
                  </ChantPopConfirm>
                )}
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      className: "header-white",
      dataIndex: "status",
      width: 100,
    },
    {
      title: "Giveaway",
      key: "giveaway",
      className: "header-white",
      dataIndex: "giveaway",
      width: 200,
    },
    {
      title: "Winners",
      key: "winners",
      className: "header-white",
      dataIndex: "winners",
      width: 200,
      render: (data) =>
        data.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {data.map((item, index) => {
              const nameInitials = item.name
                .split(" ")
                .map((word) => word[0])
                .join("")
                .toUpperCase();

              return (
                <div
                  key={index}
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <Avatar
                    src={item.image}
                    size={20}
                    style={{ borderRadius: 50 }}
                    onError={() => {
                      return true; // This prevents the default fallback image
                    }}
                  >
                    {nameInitials}
                  </Avatar>
                  {item?.name}
                  {item?.email && (
                    <Tooltip
                      title={
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {item.email}
                          <CopyOutlined
                            style={{ cursor: "pointer", flexShrink: 0 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(
                                item.email,
                                "Email copied to clipboard",
                              );
                            }}
                          />
                        </span>
                      }
                    >
                      <MailOutlined
                        style={{
                          marginLeft: 6,
                          color: "#888",
                          fontSize: 13,
                          cursor: "default",
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        ) : null,
    },
    {
      title: "Raised/Goal",
      key: "raised",
      className: "header-white",
      width: 150,
      render: (data) => {
        if (data?.goalAmount === 0) return "";

        const formatAmount = (amount) => (amount === 0 ? "0" : (amount ?? "-"));

        return `${groupCurrency}${formatAmount(data?.receivedAmount)} / ${groupCurrency}${data?.goalAmount}`;
      },
    },
    {
      title: "# Entrants",
      key: "entrantsCount",
      className: "header-white",
      dataIndex: "entrantsCount",
      width: 100,
    },
    {
      title: (
        <Tooltip title="Number of winners to be selected" placement="topLeft">
          # Winners
        </Tooltip>
      ),
      key: "winnersCount",
      className: "header-white",
      dataIndex: "winnersCount",
      width: 100,
    },
    {
      title: "Proceeds Support",
      key: "proceedsSupport",
      className: "header-white",
      dataIndex: "proceedsSupport",
      width: 150,
      ellipsis: true,
      render: (data, record) => {
        const img = `https://firebasestorage.googleapis.com/v0/b/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET}/o/${groupData?.id}%2Fpartner%2F${record?.partnerId}%2Flogo?alt=media`;

        return (
          <>
            {record?.partnerId ? (
              <div className="flex-center">
                <img
                  src={img}
                  alt={data?.name}
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "cover",
                  }}
                />
              </div>
            ) : (
              <span>{data?.name}</span>
            )}
          </>
        );
      },
    },
    {
      title: "Donated By",
      key: "sponsor",
      className: "header-white",
      dataIndex: "sponsor",
      width: 150,
      render: (data) => {
        return data?.image ? (
          <div className="flex-center">
            <img
              src={data?.image}
              alt="sponsor"
              style={{
                width: "50px",
                height: "50px",
                objectFit: "cover",
              }}
            />
          </div>
        ) : (
          <span>{data?.name}</span>
        );
      },
    },
    {
      title: "Post Date",
      key: "postDate",
      className: "header-white",
      dataIndex: "postDate",
      width: 150,
      render: (data) =>
        getDayjsFromTimestamp(data)
          ? getDayjsFromTimestamp(data).format("Do MMM, YYYY")
          : "",
    },
    {
      title: "Close Date",
      key: "closedDate",
      className: "header-white",
      dataIndex: "closedDate",
      width: 150,
      render: (data) =>
        getDayjsFromTimestamp(data)
          ? getDayjsFromTimestamp(data).format("Do MMM, YYYY")
          : "",
    },
  ];

  const fetchGiveaways = async () => {
    try {
      setLoading(true);

      // Reference to the collection
      const giveawaysRef = collection(db, `giveaways/${groupData?.id}/events`);
      const giveawaysSnapshot = await getDocs(giveawaysRef);

      const giveaways = giveawaysSnapshot.docs.map((giveawayDoc) => {
        const giveawaysData = giveawayDoc.data();

        let status;
        if (giveawaysData.isDraft) {
          status = "Draft";
        } else if (giveawaysData.isLive) {
          status = "Live";
        } else if (giveawaysData.isCompleted) {
          status = "Closed";
        }

        const giveawayCloseDate =
          giveawaysData.winnersList && giveawaysData.winnersList.length > 0
            ? giveawaysData.winnersList.find(
                (item) => item.addedOn && item.addedOn > 0,
              )?.addedOn || ""
            : "";

        return {
          id: giveawayDoc.id,
          status: status,
          partnerId: giveawaysData?.partnerId,
          postDate: giveawaysData?.addedOn,
          closedDate: giveawayCloseDate,
          giveaway: giveawaysData?.itemName,
          winnersCount: giveawaysData?.quantity || 0,
          entrantsCount: giveawaysData?.noOfParticipants || 0,
          goalAmount: giveawaysData?.goalAmount || 0,
          receivedAmount: giveawaysData?.receivedAmount || 0,
          sponsor: {
            name: giveawaysData?.donatedByText,
            image: giveawaysData?.donatedByImage,
          },
          proceedsSupport: {
            name: giveawaysData?.proceedsSupportName,
            image: giveawaysData?.proceedsSupportImage,
          },
          winners:
            giveawaysData?.winnersList?.map((item) => ({
              name: item?.userName || "",
              image: item?.userImage || "",
              email: membersByMemberId?.[item?.userDocId]?.email || "",
            })) || [],
        };
      });

      giveaways.sort((a, b) => (b.postDate || 0) - (a.postDate || 0));

      setAllGiveaways(giveaways);
    } catch (error) {
      logger.error("Error fetching giveaways:", error);
      setAllGiveaways([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGiveaway = async (id) => {
    try {
      await deleteAdminGiveaway(id, groupData?.id);
      toast.success("Giveaway deleted successfully");
      fetchGiveaways();
    } catch (error) {
      toast.error("Error deleting giveaway");
    }
  };

  useEffect(() => {
    if (
      groupData?.id &&
      Object.keys(membersByMemberId || {}).length > 0 &&
      !membersLoading
    ) {
      fetchGiveaways();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id, membersByMemberId, membersLoading]);

  return (
    <div style={{ padding: "12px" }}>
      <ScreenHeader
        title="Giveaways"
        elementCount={allGiveaways?.length}
        actions={
          <>
            {isMobile() ? (
              <IconContainer
                iconComponent={<PlusOutlined />}
                onClick={() => navigate("/admin/content/giveaways/create")}
                iconStyle={{ fontSize: "16px" }}
                disabled={loading}
              />
            ) : (
              <ChantButton
                onClick={() => navigate("/admin/content/giveaways/create")}
              >
                Create Giveaway
              </ChantButton>
            )}
          </>
        }
      />
      <div className="admin-poll-description">
        <DescriptionText>
          Create and publish giveaways here or in the app. Manage giveaway flow
          from the app (10 min warning, close, select/reselect/publish winners).
          Groups with Stripe can sell &apos;additional entries&apos; (local laws
          permitting). Feature &apos;Donated by&apos; and &apos;Proceeds
          support&apos; partners.
        </DescriptionText>
      </div>
      <Table
        bordered
        columns={columns}
        loading={loading || membersLoading}
        dataSource={allGiveaways}
        pagination={{
          position: ["bottomLeft"],
          defaultPageSize: 100,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
        }}
        rowKey="id"
        key="id"
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default AdminGiveaways;
